import { Fragment, ReactElement, useContext, useEffect, useState } from "react";
import {
  IColumn,
  IEditionResponseWithContent,
  IFilterData,
  IParentSize,
  IState
} from "../../../../../types";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import FilterComponentBase from "../../../../../components/filterComponentBase";
import { useSelector } from "react-redux";
import { useAsyncFetch } from "../../../../../system/useFetch";
import SkeletonView from "../../../../../components/skeletonView";
import StyledTableRow from "../../../../../components/styledTableRow";
import { IShiftReportEdited, IShiftReportRM } from "./types";
import { cloneObject, exportFile, formatDate } from "../../../../../system/utils";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ShiftReportEditor from "./shiftReportEditor";
import PublishIcon from "@mui/icons-material/Publish";
import { exportToPDF } from "../../../../../system/pdfController";
import { generateDefinition } from "./utils";
import { SystemContext } from "../../../../../context/systemContext";
import LockOpenIcon from "@mui/icons-material/LockOpen";

interface IProps {
  parentSize: IParentSize;
}

const columns: IColumn[] = [
  {
    id: "factoryDate",
    label: "Date",
    minWidth: 170
  },
  {
    id: "shift",
    label: "Shift",
    minWidth: 100
  },
  {
    id: "supervisor",
    label: "Supervisor",
    minWidth: 200
  },
  {
    id: "field1",
    label: "Safety/Environmental Events",
    minWidth: 200,
  },
  {
    id: "field2",
    label: "Quality Events",
    minWidth: 200,
  },
  {
    id: "field3",
    label: "Maintenance Events",
    minWidth: 200,
  },
];

const ShiftReport = ({ parentSize }: IProps): ReactElement => {
  const systemContext  = useContext(SystemContext);
  const filter = useSelector<IState, IFilterData>(state => state.filter);
  const [ reports, setReports ] = useState<IShiftReportRM[] | null>(null);
  const [ editReport, setEditReport ] = useState<IShiftReportRM | null>(null);
  const [ dispatchAPI ] = useAsyncFetch<IEditionResponseWithContent<IShiftReportRM>>();
  const [ requestReports, { loading } ]  = useAsyncFetch<IShiftReportRM[]>();
  const tableContainerHeight = parentSize.height - 50;

  const canUnlock = systemContext.user && systemContext.user.permissions.find(x => x.code === "SHIFT_REPORT_MILL");

  useEffect(() => {
    requestReports({
      module: "shiftReport",
      query: "getReportsByFilter",
      payload: { ...filter.remote }
    }).then(data => {
      setReports(cloneObject((data ?? []).sort((a, b) => {
        const diffDay = a.shift.factoryDay - b.shift.factoryDay;
        if (diffDay === 0) {
          return a.shift.name.localeCompare(b.shift.name);
        }

        return diffDay;
      })));
    });
  }, [filter.remote, requestReports]);

  const onEditReport = (item: IShiftReportRM) => {
    if (!item.isSubmitted) {
      setEditReport(item);
    }
  };

  const onDownloadPDF = (item: IShiftReportRM) => {
    const fn = async () => {
      const def = generateDefinition(item);
      const base64 = await exportToPDF(def);

      exportFile(base64, `shift report-${item.area.code.toLowerCase()}-${item.shift.name.toLowerCase()}-${formatDate(item.shift.factoryDay)}.pdf`, );
    };

    fn().then();
  };

  const onSubmit = (item: IShiftReportRM) => {
    systemContext.showMessage({
      title: "Submit Report",
      color: "warning",
      message: "Do you want to submit the Report?\nAfter submitting the report it cannot be edited. ",
      callback: () => {
        const def = generateDefinition(item);

        exportToPDF(def).then(base64 => {
          dispatchAPI({
            module: "shiftReport",
            query: "submittedReport",
            payload: {
              reportID: item.shiftReportID,
              base64,
              notifyToTopic: "SHIFT_REPORT_RM"
            }
          }).then(response => {
            setEditReport(null);
            if (response?.result) {
              setReports((reports ?? []).map(row => {
                if (row.shiftReportID === item.shiftReportID) {
                  row.isSubmitted = true;
                }

                return row;
              }));
            }
          });
        });
      }
    });
  };

  const onUnlock = (item: IShiftReportRM) => {
    systemContext.showMessage({
      title: "Submit Report",
      color: "warning",
      message: "Do you want to unlock the Report?",
      callback: () => {
        dispatchAPI({
          module: "shiftReport",
          query: "unlockReport",
          payload: {
            reportID: item.shiftReportID
          }
        }).then(response => {
          setEditReport(null);
          if (response?.result) {
            setReports((reports ?? []).map(row => {
              if (row.shiftReportID === item.shiftReportID) {
                row.isSubmitted = false;
              }

              return row;
            }));
          }
        });
      }
    });
  };

  const onAccept = (report: IShiftReportEdited) => {
    dispatchAPI({
      module: "shiftReport",
      query: "insertOrUpdateReport",
      payload: { report, notify: false }
    }).then(response => {
      setEditReport(null);
      console.log(response);
      if (response?.result) {
        setReports((reports ?? []).map(row => {
          console.log(row.shiftReportID, report.shiftReportID);
          if (row.shiftReportID === report.shiftReportID) {
            return response.content ?? row;
          }

          return row;
        }));
      }
    });
  };

  return (
    <div className="display-flex-column">
      <FilterComponentBase loading={false} disabled={{ crew: true, shift: true }}/>
      { loading && <SkeletonView  parentSize={parentSize}/> }
      { !loading &&
        <TableContainer style={{ height: tableContainerHeight, maxHeight: tableContainerHeight }}>
          <Table stickyHeader={true}  size={"small"}>
            <TableHead>
              <TableRow>
                {columns.map((column) => {
                  return (
                    <TableCell
                      key={`${column.id}`}
                      style={{ backgroundColor: "#b0bec5" , minWidth: column.minWidth }}>
                      {column.id !== "actions" ? column.label : <Fragment /> }
                    </TableCell>
                  );
                })}
                <TableCell style={{ backgroundColor: "#b0bec5", minWidth: 60 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {reports && reports.map(row => {
                return (
                  <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`shift-row-${row.shiftReportID}`} onDoubleClick={() => onEditReport(row)}>
                    <TableCell>
                      {formatDate(row.shift.factoryDay)}
                    </TableCell>
                    <TableCell>
                      {row.shift.name}
                    </TableCell>
                    <TableCell>
                      {row.user && row.user.userName}
                    </TableCell>
                    <TableCell>
                      {row.content && row.content.field1}
                    </TableCell>
                    <TableCell>
                      {row.content && row.content.field2}
                    </TableCell>
                    <TableCell>
                      {row.content && row.content.field3}
                    </TableCell>
                    <TableCell>
                      <div className="display-flex-row gap-1">
                        <Tooltip title="Edit">
                          <span>
                            {/*<IconButton size={"small"} onClick={() => onEditReport(row)} color="info">*/}
                            <IconButton size={"small"} disabled={row.isSubmitted} onClick={() => onEditReport(row)} color="info">
                              <EditIcon/>
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Submit">
                          <span>
                            <IconButton size={"small"} disabled={row.isSubmitted || !row.content} onClick={() => onSubmit(row)} color="success">
                              <PublishIcon/>
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="PDF">
                          <span>
                            {/*<IconButton size={"small"} onClick={() => onDownloadPDF(row)} color="secondary">*/}
                            <IconButton size={"small"} disabled={!row.isSubmitted} onClick={() => onDownloadPDF(row)} color="secondary">
                              <PictureAsPdfIcon/>
                            </IconButton>
                          </span>
                        </Tooltip>
                        { canUnlock &&
                          <Tooltip title="Unlock">
                            <span>
                              <IconButton size={"small"} disabled={!row.isSubmitted} onClick={() => onUnlock(row)} color="warning">
                                <LockOpenIcon/>
                              </IconButton>
                            </span>
                          </Tooltip>
                        }
                      </div>
                    </TableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      }
      {editReport && <ShiftReportEditor reportData={editReport} onAccept={onAccept} onClose={() => setEditReport(null)} />}
    </div>
  );
};


export default ShiftReport;